import { MCustomClassData } from "types";
import { APP_PDF_FOOTER } from "consts";
import { PrintableReportProps, CarboneData, Base64Images, CapturedHeatmaps } from "./types";
import { roundToTwo, normalizeStatus, statusToColor } from "./status";
import { buildDisplayConditions } from "./display-conditions";
import { buildLuminanceTables, buildLuminanceSummary } from "./luminance";
import { getSubValData, buildIlluminanceTable, buildBorderData } from "./illuminance";

export type { CapturedHeatmaps, Base64Images, DisplayConditions, ImageUrls } from "./types";

/**
 * Transform PrintableReport props to Carbone data structure
 * @param props - The PrintableReport component props
 * @param t - Translation function
 * @param _rootUrl - Deprecated - using base64Images for all images now
 * @param heatmaps - Optional captured heatmaps (base64 encoded PNGs from DOM)
 * @param lang - Language code
 * @param reportName - Report name
 * @param base64Images - Optional pre-converted base64 images
 */
export function transformToCarboneData(
  props: Partial<PrintableReportProps>,
  t: (key: string) => string,
  _rootUrl?: string,
  heatmaps?: CapturedHeatmaps,
  lang?: string,
  reportName?: string,
  base64Images?: Base64Images
): CarboneData {
  const {
    projectConfig,
    project,
    roadwayConfig,
    roadLayoutConfig,
    pos,
    delta,
    n_lums,
    lph,
    overhang,
    tilt,
    maintenanceFactor,
    article_number,
    var_number,
    light_flux,
    power,
    loadperkm,
    lampset,
    lightingClassConfig,
    s_state,
    e_state_left,
    e_state_right,
    luminance,
    luminance_summary,
    illuminance,
    sidewalk_placing,
    subValResult,
    displayLuminance,
    displayIlluminance,
    ulor,
    f_ti,
  } = props;

  const currentDate = new Date().toLocaleDateString(lang === "de" ? "de-DE" : "en-GB");

  const displayConditions = buildDisplayConditions({
    roadLayoutConfig,
    lightingClassConfig,
    luminance,
    sidewalk_placing,
    illuminance,
    displayLuminance,
    displayIlluminance,
    projectConfig,
    project,
  });

  const { hasBorderAreaLeft, hasBorderAreaRight } = displayConditions;

  const roadClass = lightingClassConfig?.roadClasses || "";
  const customRoadClass = lightingClassConfig?.customClasses?.mainCustomClass as MCustomClassData | undefined;
  const ulorValue = ulor ? Math.round(Number(ulor)) : 0;

  const illuminanceData = illuminance as Record<string, any> | undefined;
  const calcFieldDimX = illuminanceData?.["dim.x"] ? roundToTwo(Number.parseFloat(illuminanceData["dim.x"])) : 0;
  const calcFieldDimY = illuminanceData?.["dim.y"] ? roundToTwo(Number.parseFloat(illuminanceData["dim.y"])) : 0;
  const calcFieldPtsX = illuminanceData?.["pts.x"] ? Number.parseInt(illuminanceData["pts.x"]) : 0;
  const calcFieldPtsY = illuminanceData?.["pts.y"] ? Number.parseInt(illuminanceData["pts.y"]) : 0;

  const luminanceTables = buildLuminanceTables({
    luminance,
    heatmaps,
    roadClass,
    customRoadClass,
    calcFieldDimX,
    calcFieldDimY,
    calcFieldPtsX,
    calcFieldPtsY,
    ulor,
    t,
  });

  const luminanceSummaryData = luminance_summary as Record<string, unknown> | undefined;
  const firstLaneKey = luminance ? Object.keys(luminance as Record<string, unknown>)[0] : undefined;
  const firstLaneData = firstLaneKey ? (luminance as Record<string, Record<string, unknown>>)[firstLaneKey] : undefined;
  const summaryDimX = firstLaneData?.["dim.x"] ? roundToTwo(Number.parseFloat(String(firstLaneData["dim.x"]))) : calcFieldDimX;
  const summaryDimY = firstLaneData?.["dim.y"] ? roundToTwo(Number.parseFloat(String(firstLaneData["dim.y"]))) : calcFieldDimY;
  const summaryPtsX = firstLaneData?.["pts.x"] ? Number.parseInt(String(firstLaneData["pts.x"])) : calcFieldPtsX;
  const summaryPtsY = firstLaneData?.["pts.y"] ? Number.parseInt(String(firstLaneData["pts.y"])) : calcFieldPtsY;

  const luminanceSummaryObj = buildLuminanceSummary({
    luminanceSummaryData,
    firstLaneData,
    summaryDimX,
    summaryDimY,
    summaryPtsX,
    summaryPtsY,
    roadClass,
    customRoadClass,
    lightingClassConfig,
    ulor,
    ulorValue,
    s_state,
    e_state_left,
    e_state_right,
    hasBorderAreaLeft,
    hasBorderAreaRight,
    t,
  });

  const illuminanceTable = buildIlluminanceTable({ illuminanceData, lightingClassConfig, roadClass, heatmaps, f_ti, ulor, t });

  const ftiValue = f_ti ? roundToTwo(Number(f_ti)) : null;
  const rulValue = ulor ? `${Math.round(Number(ulor))}%` : "";

  const leftBorder = buildBorderData({
    side: "left",
    sidewalkData: getSubValData(subValResult, "sd-fpath-left"),
    lightingClassConfig,
    heatmaps,
    ftiValue,
    rulValue,
    t,
  });

  const rightBorder = buildBorderData({
    side: "right",
    sidewalkData: getSubValData(subValResult, "sd-fpath-right"),
    lightingClassConfig,
    heatmaps,
    ftiValue,
    rulValue,
    t,
  });

  const emptyBorder = {
    target_class: "",
    target_e_av: "",
    e_av: "",
    e_av_status: "not-applicable" as const,
    e_av_status_text: "",
    e_av_status_color: statusToColor("not-applicable"),
    target_e_min: "",
    e_min: "",
    e_min_status: "not-applicable" as const,
    e_min_status_text: "",
    e_min_status_color: statusToColor("not-applicable"),
    u_o: "",
    u_d: "",
    f_ti: "",
    r_ul: "",
    target_u_o: "",
  };

  return {
    header: {
      logo_base64: base64Images?.logo_base64,
      title: t("Lighting Calculation"),
      date: currentDate,
    },
    project: {
      project_id: projectConfig?.project_id,
      project_name: project?.project_name,
      location: project?.location,
      name_of_road: projectConfig?.name_of_road,
      comment: projectConfig?.comment,
    },
    roadway_info: {
      road_width: roadwayConfig?.roadWidth || 0,
      width_of_central_reservation: roadwayConfig?.widthOfCentralReservation,
      number_of_lanes: roadwayConfig?.numberOfLanes || 0,
      covering: roadwayConfig?.covering || "",
      q0: roadwayConfig?.q0 || 0,
      border_area_left_width: roadwayConfig?.borderAreaLeftWidth,
      border_area_left_kerb_distance: roadwayConfig?.borderAreaLeftKerbDistance,
      border_area_right_width: roadwayConfig?.borderAreaRightWidth,
      border_area_right_kerb_distance: roadwayConfig?.borderAreaRightKerbDistance,
      carriage_way: roadLayoutConfig?.carriageWay || "",
      layout_image_base64: base64Images?.layout_image_base64,
    },
    luminaire_row: {
      distance_between_poles: delta || 0,
      n_of_luminaires: n_lums,
      height_of_light_point: lph || 0,
      overhang: overhang || 0,
      tilt: tilt || 0,
      maintenance_factor: maintenanceFactor || 0,
      position: pos || "side",
      luminaire_ref_image_base64: base64Images?.luminaire_ref_image_base64,
      luminaire_ref_rope_image_base64: base64Images?.luminaire_ref_rope_image_base64,
    },
    luminaire_found: {
      article_number: article_number || "",
      ldt_file_name: var_number,
      luminous_flux: light_flux || 0,
      power_consumption: power || 0,
      load_per_km: Number(loadperkm).toFixed(2) || 0,
      lampset: lampset || "",
      family_image_base64: base64Images?.family_image_base64,
      lvk_image_base64: base64Images?.lvk_image_base64,
    },
    lighting_class: {
      road_classes: lightingClassConfig?.customClasses?.mainCustomClass?.class_type || lightingClassConfig?.roadClasses || "",
      border_area_left_classes: lightingClassConfig?.customClasses?.leftBorderCustomClass?.class_type || lightingClassConfig?.borderAreaLeftClasses || "",
      border_area_right_classes: lightingClassConfig?.customClasses?.rightBorderCustomClass?.class_type || lightingClassConfig?.borderAreaRightClasses || "",
      s_state: normalizeStatus(s_state),
      s_state_color: statusToColor(normalizeStatus(s_state)),
      e_state_left: normalizeStatus(e_state_left),
      e_state_left_color: statusToColor(normalizeStatus(e_state_left)),
      e_state_right: normalizeStatus(e_state_right),
      e_state_right_color: statusToColor(normalizeStatus(e_state_right)),
      colors: { green: "#00AA00", orange: "#FF8C00", red: "#CC0000" },
    },
    luminance_summary: luminanceSummaryObj,
    luminance_tables: luminanceTables,
    illuminance_table: {
      calculation_field: {
        dim_x: calcFieldDimX,
        dim_y: calcFieldDimY,
        pts_x: calcFieldPtsX,
        pts_y: calcFieldPtsY,
        formatted: `${calcFieldDimX} m x ${calcFieldDimY} m (${calcFieldPtsX} x ${calcFieldPtsY} ${t("pts")})`,
      },
      summary: illuminanceTable.summary,
      target_values: illuminanceTable.target_values,
      heatmap_base64: illuminanceTable.heatmap_base64,
      left_border: leftBorder ?? emptyBorder,
      right_border: rightBorder ?? emptyBorder,
    },
    footer: {
      text: t(`pdf_footer.${APP_PDF_FOOTER}`),
      additional_info: APP_PDF_FOOTER === "signify" ? t("Calculation made with RELUX") : "",
    },
    displayConditions,
    translations: {
      lighting_calculation: t("Lighting Calculation"),
      project_id: t("Project ID"),
      project_name: t("Project Name"),
      project_location: t("Project Location"),
      name_of_road: t("Name of road"),
      comment: t("Comment"),
      roadway_info: t("Roadway Info"),
      road_width: t("Road Width"),
      central_reservation: t("Width Of Central Reservation"),
      number_of_lanes: t("Number of Lanes"),
      covering: t("Covering"),
      border_area_l_width: t("Border Area L Width"),
      kerb_distance_left: t("Kerb distance left"),
      border_area_r_width: t("Border Area R Width"),
      kerb_distance_right: t("Kerb distance right"),
      luminaire_row: t("Luminaire row"),
      distance_between_poles: t("Distance between poles"),
      n_of_luminaires: t("N of luminaires"),
      height_of_light_point: t("Height of the light point"),
      overhang: t("Overhang"),
      tilt_of_luminaire: t("Tilt of the luminaire"),
      maintenance_factor: t("Maintenance factor"),
      luminaire_found: t("Luminaire found"),
      article_number: t("Article Number"),
      ldt_file_name: t("LDT file name"),
      luminous_flux: t("Luminous flux"),
      power_consumption: t("Power Consumption"),
      load_per_km: t("Load per km"),
      lampset: t("Lampset"),
      left_border_area: t("Border area left"),
      right_border_area: t("Border area right"),
      border_area_left: t("Border area left"),
      border_area_right: t("Border area right"),
      Luminance_calculation_field: t("Luminance calculation field:"),
      illuminance_calculation_field: t("Illuminance calculation field:"),
      illuminance_calculation_field_heatmap: t("Illuminance calculation field (heatmap diagram):"),
      static_illuminance: t("In M and C classes Uo is normative. Ud is in all classes informative."),
      road: t("Road"),
      roadway: t("Roadway"),
      target_value: t("Target Value"),
      y_position: t("y position"),
      pts: t("pts"),
      result_date: t("Result Date"),
      met: t("met"),
      not_met: t("not met"),
      too_high: t("too high"),
      met_sentence: t("the values of class"),
      met_sentence_suffixe: t("are"),
      target_reach_info: t("Target reach information"),
    },
    convertTo: "pdf",
    timezone: "Europe/Athens",
    lang: lang || "en",
    complement: {},
    variableStr: "",
    reportName: reportName || "Calculation Results",
    enum: {},
    currencySource: "",
    currencyTarget: "",
    currencyRates: {},
    hardRefresh: "",
  };
}
